import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import gql from 'graphql-tag';
import { useStoreState } from 'easy-peasy';

import client from '../../utils/apolloClient';
import { Input } from '../elements';

const searchUnassignPlacesQuery = gql`
  query SearchUnassignPlaces($input: SearchInput) {
    search_unassign_places(input: $input) {
      unassign_place_listing {
        place_id
        status
        name
        address_line_1
        address_line_2
        city
        state
        country
        post_code
      }
    }
  }
`;

const fetchPlaces = async ({ input }) => {
  try {
    const data = await client.clientPrivate.query({
      query: searchUnassignPlacesQuery,
      variables: { input },
      fetchPolicy: 'network-only',
    });
    return data?.data?.search_unassign_places?.unassign_place_listing ?? [];
  } catch {
    return [];
  }
};

const Wrapper = styled.div`
  position: relative;
`;

const ResultsList = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 10;
  background: white;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  max-height: 200px;
  overflow-y: auto;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`;

const ResultItem = styled.li`
  padding: 8px;
  cursor: pointer;
  color: black;

  &:hover,
  &.active {
    background: #00aeef;
    color: #f6f6f6;
  }
`;

const PlaceSearchSelect = ({ onSelect }) => {
  const { userId } = useStoreState(state => state.auth);

  const [query, setQuery] = useState('');
  const [collapsed, setCollapsed] = useState(false);
  const [touched, setTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [places, setPlaces] = useState([]);

  const debounceRef = useRef(null);

  const buildInput = value => ({
    user_id: userId,
    from: 0,
    size: 10,
    sort: 'CREATED_DESC',
    filter: {
      place_filter: {
        place_name: value,
      },
    },
  });

  return (
    <Wrapper>
      <Input
        label="Place Name"
        type="text"
        value={query}
        onChange={e => {
          const { value } = e.target;
          setCollapsed(false);
          setTouched(true);
          setQuery(value);
          setLoading(true);
          clearTimeout(debounceRef.current);
          debounceRef.current = setTimeout(async () => {
            const temp = await fetchPlaces({ input: buildInput(value) });
            setPlaces(temp);
            setLoading(false);
          }, 300);
        }}
        placeholder="Search places..."
      />
      {touched && (
        <>
          {loading ? (
            <ResultsList>
              <ResultItem>loading...</ResultItem>
            </ResultsList>
          ) : (
            <ResultsList>
              {!collapsed && (
                <>
                  {places.map(place => (
                    <ResultItem
                      key={place.place_id}
                      onClick={() => {
                        setTouched(false);
                        setCollapsed(true);
                        setQuery(place.name);
                        onSelect(place);
                      }}
                    >
                      {place.name}
                      <div style={{ fontSize: '0.8em', opacity: 0.8 }}>
                        {[
                          place.address_line_1,
                          place.city,
                          place.state,
                          place.country,
                          place.post_code,
                        ]
                          .filter(Boolean)
                          .join(', ')}
                      </div>
                    </ResultItem>
                  ))}
                  {places.length === 0 && <ResultItem>No results</ResultItem>}
                </>
              )}
            </ResultsList>
          )}
        </>
      )}
    </Wrapper>
  );
};

export default PlaceSearchSelect;
